<template>
  <AppStack
    :class="cellClasses"
    direction="flex-col"
    class="relative gap-2 bg-bg-elevation-2 px-3 pt-[10px] pb-[10px] rounded-[4px]
      shadow-elevation-1-0-xs group overflow-hidden transition-opacity duration-200
      ease-in-out"
    @click="onTap"
  >
    <div
      :class="
        props.selected
          ? ['border-2', 'border-border-blue', 'border-solid']
          : ['opacity-0']
      "
      class="absolute top-0 left-0 w-full h-full rounded-[4px] transition-opacity
        duration-200 ease-in-out"
    />
    <div
      class="absolute top-0 left-0 w-full h-full bg-bg-elevation-2-alt opacity-0
        group-active:opacity-50"
    />
    <AppTypography
      :text="props.title"
      color="text-fg-elevation-2-primary"
      name="text-md-4-semibold"
      class="relative line-clamp-3 !tracking-tighter min-h-[54px]"
    />
    <!-- Footer: price on the left, on-stop label on the right when
         flagged. Keeps both pieces in the same baseline row instead
         of growing the cell vertically. -->
    <div class="relative mt-auto flex items-center justify-between gap-2">
      <AppTypography
        :text="props.subtitle"
        color="text-fg-elevation-2-secondary"
        name="text-md-4-medium"
      />
      <AppLabel
        v-if="props.stopped"
        :text="props.stopLabel"
        variant="fill-red-faded"
      />
    </div>
    <transition
      enter-active-class="duration-500 ease-in-out"
      enter-from-class="transform opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="duration-500 ease-in-out"
      leave-from-class="opacity-100"
      leave-to-class="transform opacity-0"
    >
      <AppStack
        v-if="adding"
        direction="flex-col"
        align-items="items-center"
        justify-items="justify-center"
        class="absolute top-0 left-0 w-full h-full pointer-events-none"
      >
        <div class="absolute top-0 left-0 w-full h-full bg-bg-elevation-2" />
        <AppIcon
          size="custom"
          icon="check"
          stroke-width="2px"
          color="text-fg-blue"
          class="w-[22px] h-[22px] relative"
        />
        <AppTypography
          :text="$t('order.added')"
          color="text-fg-elevation-2-primary"
          name="text-sm-5-semibold"
          class="relative"
        />
      </AppStack>
    </transition>
    <div
      class="absolute top-0 left-0 w-full h-full opacity-0"
      @contextmenu="($event) => $event.preventDefault()"
    />
  </AppStack>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import AppStack from '@restify/packages/design-system/low-level/AppStack.vue'
import AppIcon from '@restify/packages/design-system/low-level/AppIcon.vue'
import AppTypography from '@restify/packages/design-system/low-level/AppTypography.vue'
import AppLabel from '@restify/packages/design-system/low-level/AppLabel.vue'

const emit = defineEmits<{
  'menu-item-tap': [{ onClick: typeof onClick }]
  'stopped-tap': []
}>()

const props = withDefaults(
  defineProps<{
    added?: boolean
    selected?: boolean
    faded?: boolean
    stopped?: boolean
    stopLabel?: string
    title: string
    subtitle: string
  }>(),
  {
    added: false,
    selected: false,
    faded: false,
    stopped: false,
    stopLabel: '',
  },
)

const adding = ref(false)

const onClick = () => {
  adding.value = true

  setTimeout(() => (adding.value = false), 1000)
}

// Faded includes the "stopped" state — the cell gets dimmed and the red
// badge overlay is applied. Tap on a stopped cell raises a separate
// event so the parent can show the kitchen's reason in a sheet instead
// of routing through the regular add-to-order flow.
const cellClasses = computed(() => {
  const classes: string[] = []
  if (props.faded || props.stopped) classes.push('opacity-50')

  return classes
})

const onTap = () => {
  if (props.stopped) {
    emit('stopped-tap')

    return
  }

  emit('menu-item-tap', { onClick })
}
</script>
